import chunk from 'lodash.chunk';
import { delay, put, select } from 'redux-saga/effects';
import { Base64 } from 'js-base64';

import {
  fetchSignPlan,
  fetchSignTestPlan,
  getPlanXlsxById,
  getPlanXmlById,
  ISignPlanError,
  signPlanXlsxById,
} from 'APIPackage/PlanListModule/services/knmPlanApi';
import { subscribe } from 'KnmPackage/MainModule/common/cryptoHelpers';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { convertFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertFileToBase64';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import {
  fetchKnmDocumentSignaturesUpload,
  fetchKnmDocumentAttachmentUpload,
} from 'APIPackage/KnmModule/services/knmDocumentAttachmentApi';
import { planListGetData } from 'PlanListPackage/MainModule/actions/planListActions';
import { HANDLING_PLAN } from 'CorePackage/MainModule/constants/notices';
import { knmSignFunc, knmTestSignFunc } from 'KnmPackage/MainModule/common/knmSignFunc';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import { coreSetLoadingProgress } from 'CorePackage/MainModule/actions/coreActions';
import { fetchUnsignedKnmIdsByPlanId } from 'APIPackage/KnmModule/services/knmPlanApi';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';

import { planInitPage } from '../actions/planActions';
import { IValidatedUnsignedKnmError } from '../../../APIPackage/KnmModule/interfaces/IValidatedUnsignedKnmByPlan';
import { IPlanFormValues } from '../interfaces/PlanForm';

const WARNING_KNMS_SIZE = 200;
const SIZE_BATCH = 10;

function* signPlanBatch(knmIds: number[], certificate: string, rejectedArr: number[], form?: IPlanFormValues) {
  const actions = knmIds.map(async knmId => {
    try {
      return await knmSignFunc(certificate, true, knmId, undefined, undefined, form, undefined, undefined, true);
    } catch (err) {
      const currentError = err as IResponseError;

      if (currentError.status === 401 || currentError.status === 403) {
        rejectedArr.push(knmId);
      } else {
        return Promise.reject();
      }
    }
  });

  yield Promise.all(actions);
}

type PageParams =
  | {
      page: 'plan';
    }
  | {
      page: 'planList';
      searchValue: string;
    };

function* signKnms(
  bacthes: number[][],
  certificate: string,
  knmIds: number[],
  blockedIteration: number = 0,
  form?: IPlanFormValues,
): Generator {
  const rejectedKnmIds: number[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const batch of bacthes) {
    yield put(
      coreSetLoadingProgress(
        Math.floor(
          (((bacthes.findIndex(item => item === batch) + 1) * batch.length < SIZE_BATCH ? batch.length : SIZE_BATCH) /
            knmIds.length) *
            100,
        ),
      ),
    );
    yield signPlanBatch(batch, certificate, rejectedKnmIds, form);
  }
  if (rejectedKnmIds.length && rejectedKnmIds.length < knmIds.length * 0.5 && blockedIteration < 5) {
    const knmIdsBatches = chunk(rejectedKnmIds, SIZE_BATCH);

    yield signKnms(knmIdsBatches, certificate, rejectedKnmIds, blockedIteration + 1, form);
  }
}

export function* planSignSaga(
  planId: number,
  certificate: string,
  pageParams: PageParams,
  errors: IValidatedUnsignedKnmError[],
  form?: IPlanFormValues,
) {
  const state: IRootState = yield select();
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
  // sign KNMs
  let knmIds: number[] = yield fetchUnsignedKnmIdsByPlanId(planId);

  if (errors.length) {
    knmIds = knmIds.filter(knmId => !errors.find(err => err.id === knmId));
  }

  if (knmIds.length > WARNING_KNMS_SIZE) {
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.SIGN_PLAN,
        renderContent: HANDLING_PLAN,
      }),
    );
  }

  const knmIdsBatches = chunk(knmIds, SIZE_BATCH);

  yield signKnms(knmIdsBatches, certificate, knmIds, 0, form);

  if (errors.length) {
    return;
  }

  // sign plan
  try {
    let planContent: Blob;

    if (isErknm) {
      planContent = yield getPlanXmlById(planId);
    } else {
      planContent = yield getPlanXlsxById(planId);
    }

    const base64Content: string = yield convertFileToBase64(planContent);
    const planSign: string = yield subscribe(base64Content, cadesplugin.CADESCOM_CADES_BES, certificate);

    if (isErknm) {
      const signPlanInfo: ISignPlanError = yield fetchSignPlan({
        id: planId,
        document: Base64.encode(base64Content),
        signature: planSign,
        file: planContent,
        fileName: `${String(planId)}.xml`,
      });

      if (signPlanInfo?.errorMessage) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.SIGN_PLAN_WARNING,
            renderContent: signPlanInfo.errorMessage,
          }),
        );
      }
    } else {
      const signInfo: IKnmAttachment = yield fetchKnmDocumentSignaturesUpload({
        document: Base64.encode(base64Content),
        signatures: [planSign],
      });

      if (signInfo.signed && signInfo.signatureInfo?.length) {
        const { signatureInfo, signed: isSigned } = signInfo;
        const uploadResult: IKnmAttachment = yield fetchKnmDocumentAttachmentUpload({
          file: planContent,
          fileName: `${String(planId)}.xlsx`,
        });

        yield signPlanXlsxById(planId, { ...uploadResult, signatureInfo, signed: isSigned });
      }
    }
    yield delay(REFRESH_DELAY);
    if (pageParams.page === 'plan') {
      yield put(planInitPage(planId.toString()));
    } else {
      yield put(planListGetData(pageParams.searchValue));
    }
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.SIGN_PLAN,
        renderContent: 'План успешно подписан',
      }),
    );
  } catch (err) {
    const error = err as IResponseError;

    if (error.clientErrorCode === 'E4102') {
      const errList = error.message?.split('ошибка = ');

      if (errList && errList.length > 1) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SIGN_PLAN,
            renderContent: errList[1],
          }),
        );
      }

      return;
    }

    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.SIGN_PLAN,
        renderContent: 'Не удалось подписать план',
      }),
    );
  }
}

function* signTestPlanBatch(knmIds: number[], rejectedArr: number[]) {
  const actions = knmIds.map(async knmId => {
    try {
      await knmTestSignFunc(knmId);
    } catch (err) {
      const currentError = err as IResponseError;

      if (currentError.status === 401 || currentError.status === 403) {
        rejectedArr.push(knmId);
      } else {
        return Promise.reject();
      }
    }
  });

  yield Promise.all(actions);
}

function* signTestKnms(bacthes: number[][], knmIds: number[], blockedIteration: number = 0): Generator {
  const rejectedKnmIds: number[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const batch of bacthes) {
    yield put(
      coreSetLoadingProgress(
        Math.floor(
          (((bacthes.findIndex(item => item === batch) + 1) * batch.length < SIZE_BATCH ? batch.length : SIZE_BATCH) /
            knmIds.length) *
            100,
        ),
      ),
    );
    yield signTestPlanBatch(batch, rejectedKnmIds);
  }
  if (rejectedKnmIds.length && rejectedKnmIds.length < knmIds.length * 0.5 && blockedIteration < 5) {
    const knmIdsBatches = chunk(rejectedKnmIds, SIZE_BATCH);

    yield signTestKnms(knmIdsBatches, rejectedKnmIds, blockedIteration + 1);
  }
}

export function* planTestSignSaga(planId: number, pageParams: PageParams) {
  const state: IRootState = yield select();
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
  // sign KNMs
  const knmIds: number[] = yield fetchUnsignedKnmIdsByPlanId(planId);

  if (knmIds.length > WARNING_KNMS_SIZE) {
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.SIGN_PLAN,
        renderContent: HANDLING_PLAN,
      }),
    );
  }
  const knmIdsBatches = chunk(knmIds, SIZE_BATCH);

  yield signTestKnms(knmIdsBatches, knmIds);

  // sign plan
  try {
    if (isErknm) {
      yield fetchSignTestPlan(planId);
    }
    yield delay(REFRESH_DELAY);
    if (pageParams.page === 'plan') {
      yield put(planInitPage(planId.toString()));
    } else {
      yield put(planListGetData(pageParams.searchValue));
    }
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.SIGN_PLAN,
        renderContent: 'План успешно подписан',
      }),
    );
  } catch (err) {
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.SIGN_PLAN,
        renderContent: 'Не удалось подписать план',
      }),
    );
  }
}
